/**
 * @file        src/pages/insurance/cells.jsx
 * @description Shared table-cell renderers + formatters for the travel-insurance
 *              admin lists. Mirrors pages/flight/cells and pages/hotel/cells so
 *              all three product modules read alike.
 * @author      Kinetika Labs
 * @created     2026-09-22
 * @changelog
 *   - 2026-09-22 Claude — Initial (B2C Insurance + Corporate Insurance).
 */
import Chip from '../../components/Chip';

export const money = (v) => (v == null || v === '' ? '—' : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/** "25 Sep 2026" — a cover date. */
export const day = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** "01:55 PM , 23 Sep 2026" — the purchase timestamp. */
export const dateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  const t = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${t} , ${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`;
};

/** Chip tone for a booking / refund status. */
export const statusTone = (s = '') =>
  /cancel|fail|reject/i.test(s) ? 'rej'
    : /pend|initiat|await|process/i.test(s) ? 'pend'
      : /book|success|complete|refund/i.test(s) ? 'ok'
        : 'warn';

/**
 * The `insurance_bookings.journey` ENUM, in the customer's own words — the
 * SAME three labels the storefront prints (its insuranceUtils.JOURNEY_LABEL),
 * so the admin and the buyer call one product the same thing. An unknown value
 * is shown raw rather than blanked: it means the ENUM grew.
 */
const JOURNEY_LABEL = { intl: 'International', student: 'Student', amt: 'Annual Multi Trip' };
export const journeyLabel = (v) => JOURNEY_LABEL[v] || (v ? String(v) : '—');

// One client-approved status→documents rule, shared with flight and hotel.
export { docAccess } from '../../lib/docAccess';

/**
 * Buyer identity. "Guest" marks a policy bought WITHOUT logging in — detected
 * from user_id, never from a missing name (a registered customer who never
 * saved a name is not a guest). Identical rule to the other two modules.
 */
export function customerCell(_v, r) {
  const name = String(r.customer_name || '').trim();
  const email = r.customer_email || '';
  const isGuest = !r.user_id || Number(r.user_id) === 1;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div>
        {name ? <div>{name}</div> : null}
        {email ? <div className={name ? 'muted' : undefined} style={name ? { fontSize: '.85em' } : undefined}>{email}</div> : null}
        {!name && !email ? <span className="muted">—</span> : null}
      </div>
      {isGuest ? <Chip tone="warn">Guest</Chip> : null}
    </div>
  );
}
